import { zeroAddress } from "viem";

import { OrderCreatedEventData, OrderStatus, OrderStatuses, PendingOrderData } from "context/SyntheticsEvents/types";
import { setByKey, updateByKey } from "lib/objects";
import { TradeAction as RawTradeAction } from "sdk/codegen/subsquid";
import { DecreasePositionSwapType, OrderType } from "sdk/utils/orders/types";
import { isMarketOrderType, isSwapOrderType } from "sdk/utils/orders/utils";
import { TradeActionType } from "sdk/utils/tradeHistory/types";

const ORDER_BACKFILL_LOOKBACK_SECONDS = 60;
export const ORDER_BACKFILL_PAGE_SIZE = 50;
export const ORDER_BACKFILL_MAX_AGE_MS = 10 * 60 * 1000;

type OrderBackfillEventName =
  | TradeActionType.OrderCreated
  | TradeActionType.OrderExecuted
  | TradeActionType.OrderUpdated
  | TradeActionType.OrderCancelled;

export const ORDER_STATUS_TXN_HASH_FIELD_BY_EVENT = {
  [TradeActionType.OrderCreated]: "createdTxnHash",
  [TradeActionType.OrderExecuted]: "executedTxnHash",
  [TradeActionType.OrderUpdated]: "updatedTxnHash",
  [TradeActionType.OrderCancelled]: "cancelledTxnHash",
} as const satisfies Record<OrderBackfillEventName, keyof OrderStatus>;

export type OrderBackfillMatch = {
  pendingOrder: PendingOrderData;
  orderKey: string;
  eventName: OrderBackfillEventName;
  transactionHash: string;
};

export function getIsPendingOrderBackfillable(order: PendingOrderData) {
  // TWAP parts need twapGroupId-aware queries and matching, which is not supported here
  if (order.isTwap) {
    return false;
  }

  return order.txnType === "create" || order.orderKey !== undefined;
}

function getExpectedEventNames(order: PendingOrderData): OrderBackfillEventName[] {
  if (order.txnType === "update") {
    return [TradeActionType.OrderUpdated];
  }

  if (order.txnType === "cancel") {
    return [TradeActionType.OrderCancelled];
  }

  // Non-market create actions stay queryable after execution or cancellation.
  return isMarketOrderType(order.orderType)
    ? [TradeActionType.OrderExecuted, TradeActionType.OrderCancelled]
    : [TradeActionType.OrderCreated];
}

export function getOrderBackfillParams(pendingOrders: PendingOrderData[]) {
  const orders = pendingOrders.filter(getIsPendingOrderBackfillable);

  if (orders.length === 0) {
    return undefined;
  }

  const combinationsByKey = new Map<
    string,
    { eventName: TradeActionType; isDepositOrWithdraw: boolean; orderTypes: Set<OrderType> }
  >();

  orders.forEach((order) => {
    // Zero-size position market orders are collateral deposits or withdrawals.
    const isDepositOrWithdraw =
      isMarketOrderType(order.orderType) && !isSwapOrderType(order.orderType) && order.sizeDeltaUsd === 0n;

    getExpectedEventNames(order).forEach((eventName) => {
      const key = `${eventName}:${isDepositOrWithdraw}`;
      const combination = combinationsByKey.get(key) ?? { eventName, isDepositOrWithdraw, orderTypes: new Set() };

      combination.orderTypes.add(order.orderType);
      combinationsByKey.set(key, combination);
    });
  });

  const orderEventCombinations = Array.from(combinationsByKey.values()).map(
    ({ eventName, isDepositOrWithdraw, orderTypes }) => ({
      eventName,
      orderType: Array.from(orderTypes),
      ...(isDepositOrWithdraw ? { isDepositOrWithdraw } : {}),
    })
  );

  const createdAt = Math.min(...orders.map((order) => order.createdAt));

  return {
    account: orders[0].account,
    fromTxTimestamp: Math.max(0, Math.floor(createdAt / 1000) - ORDER_BACKFILL_LOOKBACK_SECONDS),
    orderEventCombinations,
  };
}

export function getOrderBackfillMatches(
  pendingOrders: PendingOrderData[],
  rawActions: RawTradeAction[] | undefined,
  orderStatuses: OrderStatuses
): OrderBackfillMatch[] {
  if (!rawActions?.length) {
    return [];
  }

  const usedActionIds = new Set<string>();
  const matches: OrderBackfillMatch[] = [];

  pendingOrders.filter(getIsPendingOrderBackfillable).forEach((pendingOrder) => {
    const action = rawActions.find(
      (rawAction) =>
        !usedActionIds.has(rawAction.id) &&
        !getIsActionAlreadyApplied(rawAction, orderStatuses[rawAction.orderKey], pendingOrder) &&
        getIsRawTradeActionMatchingPendingOrder(rawAction, pendingOrder)
    );

    if (!action) {
      return;
    }

    usedActionIds.add(action.id);
    matches.push({
      pendingOrder,
      orderKey: action.orderKey,
      eventName: action.eventName as OrderBackfillEventName,
      transactionHash: action.transactionHash,
    });
  });

  return matches;
}

function getIsActionAlreadyApplied(
  rawAction: RawTradeAction,
  orderStatus: OrderStatus | undefined,
  pendingOrder: PendingOrderData
) {
  const txnHashField = ORDER_STATUS_TXN_HASH_FIELD_BY_EVENT[rawAction.eventName as OrderBackfillEventName];

  if (txnHashField === undefined || !orderStatus?.[txnHashField]) {
    return false;
  }

  // Create recoveries also need reconstructed order data so the notification can match the status.
  if (pendingOrder.txnType === "create" && !orderStatus.data) {
    return false;
  }

  return true;
}

function getIsRawTradeActionMatchingPendingOrder(rawAction: RawTradeAction, pendingOrder: PendingOrderData) {
  if (!getExpectedEventNames(pendingOrder).includes(rawAction.eventName as OrderBackfillEventName)) {
    return false;
  }

  if (pendingOrder.txnType !== "create") {
    return pendingOrder.orderKey !== undefined && rawAction.orderKey === pendingOrder.orderKey;
  }

  const isAfterSubmission =
    rawAction.timestamp + ORDER_BACKFILL_LOOKBACK_SECONDS >= Math.floor(pendingOrder.createdAt / 1000);

  const isInitialCollateralAmountMatch =
    pendingOrder.externalSwapQuote !== undefined ||
    BigInt(rawAction.initialCollateralDeltaAmount ?? 0) === pendingOrder.initialCollateralDeltaAmount;

  const isAmountsMatch =
    BigInt(rawAction.sizeDeltaUsd ?? 0) === pendingOrder.sizeDeltaUsd &&
    isInitialCollateralAmountMatch &&
    (!isSwapOrderType(pendingOrder.orderType) ||
      BigInt(rawAction.minOutputAmount ?? 0) === pendingOrder.minOutputAmount) &&
    (isMarketOrderType(pendingOrder.orderType) || BigInt(rawAction.triggerPrice ?? 0) === pendingOrder.triggerPrice);

  return (
    isAfterSubmission &&
    isAmountsMatch &&
    rawAction.orderType === pendingOrder.orderType &&
    (rawAction.decreasePositionSwapType ?? DecreasePositionSwapType.NoSwap) === pendingOrder.decreasePositionSwapType &&
    (rawAction.isLong ?? pendingOrder.isLong) === pendingOrder.isLong &&
    (rawAction.shouldUnwrapNativeToken ?? false) === pendingOrder.shouldUnwrapNativeToken &&
    rawAction.account === pendingOrder.account &&
    (rawAction.marketAddress ?? zeroAddress) === pendingOrder.marketAddress &&
    rawAction.initialCollateralTokenAddress === pendingOrder.initialCollateralTokenAddress &&
    rawAction.swapPath.length === pendingOrder.swapPath.length &&
    rawAction.swapPath.every((address, index) => address === pendingOrder.swapPath[index])
  );
}

export function getOrderCreatedDataFromPendingOrder(
  pendingOrder: PendingOrderData,
  orderKey: string
): OrderCreatedEventData {
  return {
    key: orderKey,
    account: pendingOrder.account,
    receiver: pendingOrder.account,
    callbackContract: zeroAddress,
    marketAddress: pendingOrder.marketAddress,
    initialCollateralTokenAddress: pendingOrder.initialCollateralTokenAddress,
    swapPath: pendingOrder.swapPath,
    sizeDeltaUsd: pendingOrder.sizeDeltaUsd,
    initialCollateralDeltaAmount: pendingOrder.initialCollateralDeltaAmount,
    contractTriggerPrice: pendingOrder.triggerPrice,
    contractAcceptablePrice: pendingOrder.acceptablePrice,
    executionFee: 0n,
    callbackGasLimit: 0n,
    minOutputAmount: pendingOrder.minOutputAmount,
    updatedAtBlock: 0n,
    orderType: pendingOrder.orderType,
    decreasePositionSwapType: pendingOrder.decreasePositionSwapType,
    isLong: pendingOrder.isLong,
    shouldUnwrapNativeToken: pendingOrder.shouldUnwrapNativeToken,
    isFrozen: false,
    uiFeeReceiver: zeroAddress,
    externalSwapQuote: undefined,
    isTwap: pendingOrder.isTwap,
  };
}

export function applyOrderBackfillMatches(orderStatuses: OrderStatuses, matches: OrderBackfillMatch[]): OrderStatuses {
  let next = orderStatuses;

  for (const match of matches) {
    const txnHashField = ORDER_STATUS_TXN_HASH_FIELD_BY_EVENT[match.eventName];
    const existing = next[match.orderKey];

    const needsTxnHash = !existing?.[txnHashField];
    // A recovered execution/cancellation carries no order data, so reconstruct it for create notifications.
    const needsData = match.pendingOrder.txnType === "create" && !existing?.data;

    if (!needsTxnHash && !needsData) {
      continue;
    }

    const patch: Partial<OrderStatus> = {};

    if (needsTxnHash) {
      patch[txnHashField] = match.transactionHash;
    }

    if (needsData) {
      patch.data = getOrderCreatedDataFromPendingOrder(match.pendingOrder, match.orderKey);
    }

    next = existing
      ? updateByKey(next, match.orderKey, patch)
      : setByKey(next, match.orderKey, {
          key: match.orderKey,
          createdAt: match.pendingOrder.createdAt,
          ...patch,
        });
  }

  return next;
}
